import React, { useEffect, useState, useMemo } from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery, useUpdateTherapistsUsersConnectionsMutation } from "../../services/api/user.api";
import Loader from "../loader/loader";
import styles from "../therapistPatientsTable/therapistPatientsTable.module.css";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { IUserReport } from "../../services/types/users.types";
import ListPagination from "../listPagination/listPagination";
import { TLimits } from "../../types/types";
import Modal from "../modals/modal/Modal";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";

export type AssignmentMode = 'therapist-patients' | 'patient-therapists';

interface AssignmentTableProps {
  userGuid: string;
  mode: AssignmentMode;
}

type TabType = 'assigned' | 'unassigned';

const AssignmentTable: React.FC<AssignmentTableProps> = ({ userGuid, mode }) => {
  const admin = useAppSelector(adminSelector);
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  
  // Queries
  const [getAssignedUsers, { data: assignedData, isLoading: isLoadingAssigned }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  const [getAllUsers, { data: allData, isLoading: isLoadingAll }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  
  // Mutation
  const [updateTherapistsUsersConnections] = useUpdateTherapistsUsersConnectionsMutation();
  
  // Data State
  const [assignedUsers, setAssignedUsers] = useState<IUserReport[]>([]);
  const [unassignedUsers, setUnassignedUsers] = useState<IUserReport[]>([]);

  // Pagination State
  const [limits, setLimits] = useState<TLimits>({ start: 1, end: 10 });
  const rowsPerPage = 10;

  // Checkbox State
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());

  // Search State
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Modal State
  const [skippedModal, setSkippedModal] = useState<{ isOpen: boolean; count: number }>({ isOpen: false, count: 0 });

  // Configuration based on mode
  const config = useMemo(() => {
    const isTherapistView = mode === 'therapist-patients';
    return {
      fetchRoleIds: isTherapistView ? [1] : [-1], // 1=Patient, -1=Therapist
      fetchTherapistGuidsAssigned: isTherapistView ? [userGuid] : [], 
      fetchUserGuidsAssigned: isTherapistView ? [] : [userGuid],
      title: isTherapistView ? t("THERAPIST_PATIENTS") : t("PATIENT_THERAPISTS"),
      tabAssigned: isTherapistView ? t("ASSIGNED_PATIENTS") : t("ASSIGNED_THERAPISTS"),
      tabUnassigned: isTherapistView ? t("UNASSIGNED_PATIENTS") : t("UNASSIGNED_THERAPISTS"),
      searchPlaceholder: isTherapistView ? t("SEARCH_PATIENT") : t("SEARCH_THERAPIST"),
      assignBtn: isTherapistView ? t("ASSIGN_PATIENTS") : t("ASSIGN_THERAPISTS"),
      unassignBtn: isTherapistView ? t("UNASSIGN_PATIENTS") : t("UNASSIGN_THERAPISTS"),
      selectedText: t("PATIENTS_SELECTED"), // Could be generic "SELECTED"
      noData: isTherapistView ? t("NO_PATIENTS_FOUND") : t("NO_THERAPISTS_FOUND"),
      skippedMessageKey: isTherapistView ? "PATIENTS_SKIPPED_MESSAGE" : "THERAPISTS_SKIPPED_MESSAGE"
    };
  }, [mode, userGuid]);

  // Initial Data Fetch
  useEffect(() => {
    if (userGuid && admin.companyId) {
      // Fetch Assigned
      getAssignedUsers({
        companyIds: [admin.companyId],
        roleIds: config.fetchRoleIds,
        therapistGuids: config.fetchTherapistGuidsAssigned,
        userGuids: config.fetchUserGuidsAssigned,
        fakeCompanyIds: [admin.fakeCompanyId || -1],
        isInternal: true,
        isMinimal: true,
      });

      // Fetch All (to calculate Unassigned)
      getAllUsers({
        companyIds: [admin.companyId],
        roleIds: config.fetchRoleIds,
        therapistGuids: [], 
        userGuids: [],
        fakeCompanyIds: [admin.fakeCompanyId || -1],
        isInternal: true,
        isMinimal: true,
      });
    }
  }, [userGuid, admin.companyId, admin.fakeCompanyId, getAssignedUsers, getAllUsers, config]);

  // Process Data
  useEffect(() => {
    if (assignedData?.Users) {
      setAssignedUsers(assignedData.Users);
    }
  }, [assignedData]);

  useEffect(() => {
    if (allData?.Users && assignedData?.Users) {
      const assignedIds = new Set(assignedData.Users.map(u => u.Users.Id));
      const unassigned = allData.Users.filter(u => !assignedIds.has(u.Users.Id));
      setUnassignedUsers(unassigned);
    } else if (allData?.Users) {
        setUnassignedUsers(allData.Users);
    }
  }, [allData, assignedData]);

  // Current Display Data with Search Filter
  const currentList = useMemo(() => {
    const baseList = activeTab === 'assigned' ? assignedUsers : unassignedUsers;
    
    if (!searchQuery.trim()) return baseList;
    
    const query = searchQuery.toLowerCase();
    return baseList.filter(item => 
      item.Users.FullName?.toLowerCase().includes(query) ||
      item.Users.FirstName?.toLowerCase().includes(query) ||
      item.Users.LastName?.toLowerCase().includes(query) ||
      item.Users.Phone?.includes(query)
    );
  }, [activeTab, assignedUsers, unassignedUsers, searchQuery]);
  
  // Pagination Logic
  const paginatedList = useMemo(() => {
      const startIndex = limits.start - 1;
      const endIndex = limits.end;
      return currentList.slice(startIndex, endIndex);
  }, [currentList, limits]);

  // Handle Tab Change
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setLimits({ start: 1, end: rowsPerPage }); // Reset pagination
    setSelectedUserIds(new Set()); 
  };

  // Handle Checkbox
  const toggleSelection = (id: number) => {
    const newSet = new Set(selectedUserIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedUserIds(newSet);
  };

  const handleAction = async () => {
    if (selectedUserIds.size === 0) return;

    // Get GUIDs of selected items
    const selectedItems = currentList.filter(item => 
      selectedUserIds.has(item.Users.Id)
    );
    const selectedGuids = selectedItems
      .map(p => p.Users.Guid)
      .filter((guid): guid is string => guid !== null);

    // Determine action type: 1 = assign, 2 = unassign
    const actionType = activeTab === 'assigned' ? 2 : 1;

    try {
      // Logic:
      // If mode is 'therapist-patients': therapist is fixed (userGuid), selected are patients (usersGuids)
      // If mode is 'patient-therapists': patient is fixed (userGuid), selected are therapists (therapistsGuids)
      
      const isTherapistView = mode === 'therapist-patients';
      const payload = {
          therapistsGuids: isTherapistView ? [userGuid] : selectedGuids,
          usersGuids: isTherapistView ? selectedGuids : [userGuid]
      };

      const result = await updateTherapistsUsersConnections({
        body: payload,
        type: actionType,
      }).unwrap();

      // Check if any connections were skipped
      if (result.results?.connectionsSkipped && result.results.connectionsSkipped > 0) {
        setSkippedModal({ isOpen: true, count: result.results.connectionsSkipped });
      }

      // Refresh data after successful action
      getAssignedUsers({
        companyIds: [admin.companyId],
        roleIds: config.fetchRoleIds,
        therapistGuids: config.fetchTherapistGuidsAssigned,
        userGuids: config.fetchUserGuidsAssigned,
        fakeCompanyIds: [admin.fakeCompanyId || -1],
        isInternal: true,
        isMinimal: true,
      });

      getAllUsers({
        companyIds: [admin.companyId],
        roleIds: config.fetchRoleIds,
        therapistGuids: [],
        userGuids: [],
        fakeCompanyIds: [admin.fakeCompanyId || -1],
        isInternal: true,
        isMinimal: true,
      });

      // Clear selection after action
      setSelectedUserIds(new Set());
    } catch (error) {
      console.error("Error updating connections:", error);
    }
  };

  const isLoading = isLoadingAssigned || isLoadingAll;

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.patientsTableContainer}>
      {/* <h2 className={styles.tableTitle}>{config.title}</h2>  -- Title might be redundant inside tabs */}
      
      {/* Tabs */}
      <div className={styles.filterTabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'assigned' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('assigned')}
        >
          {config.tabAssigned} ({assignedUsers.length})
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'unassigned' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('unassigned')}
        >
          {config.tabUnassigned} ({unassignedUsers.length})
        </button>
      </div>

      <div className={styles.actionContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={config.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button
          className={styles.actionButton}
          onClick={handleAction}
          disabled={selectedUserIds.size === 0}
        >
          {activeTab === 'assigned' ? config.unassignBtn : config.assignBtn}
        </button>
        {selectedUserIds.size > 0 && (
          <span className={styles.selectedCount}>
            {selectedUserIds.size} {config.selectedText}
          </span>
        )}
      </div>

      {currentList.length === 0 ? (
        <div className={styles.noData}>
          {config.noData}
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.patientsTable}>
            <thead>
              <tr className={styles.headerRow}>
                <th className={styles.headerCell}></th>
                <th className={styles.headerCell}>{t("COLUMN_ID")}</th>
                <th className={styles.headerCell}>{t("NAME_2")}</th>
                <th className={styles.headerCell}>{t("PHONE")}</th>
                <th className={styles.headerCell}>{t("COLUMN_STATUS")}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedList.map((item, index) => (
                <tr key={item.Users.Id || index} className={styles.dataRow}>
                  <td className={styles.dataCell}>
                    <div className={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedUserIds.has(item.Users.Id)}
                        onChange={() => toggleSelection(item.Users.Id)}
                      />
                    </div>
                  </td>
                  <td className={styles.dataCell}>{item.Users.Id}</td>
                  <td className={styles.dataCell}>
                    {item.Users.FullName ||
                      `${item.Users.FirstName} ${item.Users.LastName || ""}`}
                  </td>
                  <td className={styles.dataCell}>{item.Users.Phone || "-"}</td>
                  <td className={styles.dataCell}>
                    {item.Users.StatusId === 1 ? t("ACTIVE") : t("INACTIVE")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            <div className={styles.paginationContainer}>
                <ListPagination 
                    limits={limits}
                    total={currentList.length}
                    setLimits={setLimits}
                    step={rowsPerPage}
                />
            </div>
        </div>
      )}

      {/* Skipped Modal */}
      {skippedModal.isOpen && (
        <Modal onClose={() => setSkippedModal({ isOpen: false, count: 0 })}>
          <GeneralSuccessPopup
            onClose={() => setSkippedModal({ isOpen: false, count: 0 })}
            infoText={t(config.skippedMessageKey, { count: skippedModal.count }) || 
              `${skippedModal.count} skipped`}
          />
        </Modal>
      )}
    </div>
  );
};

export default AssignmentTable;
